import Link from "next/link";
import { GetServerSidePropsContext } from "next/types";

import { AppointmentType } from "@calcom/features/coaches/types";
import Shell from "@calcom/features/shell/Shell";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { Loader, Rating } from "@calcom/ui";
import { FiSearch } from "@calcom/ui/components/icon";

import { useQueryState } from "@components/getting-started/query-state";
import { MultiSelectFilter } from "@components/ui/form/MultiSelectFilter";

import { ssrInit } from "@server/lib/ssr";

const HUBSPOT_PARAMS: Record<string, Record<string, number[]>> = {
  client_primary_goal: {
    "Weight Loss": [1],
    weight_loss: [1],
    "Health Improvement": [2, 3, 4, 8],
    health_improvement: [2, 3, 4, 8],
    "Education Metabolism": [],
    education_metabolism: [],
    Other: [],
    other: [],
  },
  client_primary_obstacle: {
    "Jojo-Effekt & Heißhunger": [1],
    yoyo_effect: [1],
    "Zeit & Stress im Alltag": [],
    time_constraints: [],
    "Fehlende Motivation & Plan": [],
    accountability_needed: [],
    Other: [],
    other: [],
  },
};

export default function Search() {
  const { t } = useLocale();

  const [goals, setGoals] = useQueryState("goals");
  const [selectedMeetingOptions, setMeetingOptions] = useQueryState("meetingOptions");

  const { data } = trpc.viewer.coaches.search.useQuery(
    {
      filters: { goals, meetingOptions: selectedMeetingOptions },
    },
    {
      keepPreviousData: true,
    }
  );

  return (
    <Shell isPublic heading={t("lb_search_coach")} subtitle={t("lb_search_coach_subtitle")}>
      {!data ? <Loader /> : null}

      {!data || data.hasMatches ? null : (
        <div className="mb-6 flex w-full flex-col items-center space-y-3 rounded-md border border-dashed p-3 sm:flex-row sm:space-x-3 sm:space-y-0">
          <div className="bg-brand-100 flex h-12 w-12 shrink-0 items-center justify-center rounded-full dark:bg-white">
            <FiSearch className="text-brand-500 inline-block h-6 w-6 stroke-[1.3px] dark:bg-gray-900 dark:text-gray-600" />
          </div>
          <div className="flex flex-col items-center sm:items-start">
            <h2 className="text-semibold font-cal text-center dark:text-gray-300">
              {t("lb_search_coach_empty_title")}
            </h2>
            <div className="text-center text-sm font-normal leading-6 text-gray-700 sm:text-start dark:text-gray-300">
              {t("lb_search_coach_empty_subtitle")}
            </div>
          </div>
        </div>
      )}

      {data && data.results.length > 0 ? (
        <ul role="list" className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
          {data.results.map(({ uid, username, ...profile }) => (
            <li
              key={uid}
              className="isolate col-span-1 flex flex-col overflow-clip rounded-lg bg-white shadow">
              <Link className="contents" href={`/coach/${username}`} target="_blank">
                <div className="relative flex overflow-clip">
                  <img
                    className="max-h-48 w-full self-center object-contain"
                    src={profile.avatar ?? ""}
                    alt=""
                  />
                  <div
                    className="absolute inset-0 z-[-1] blur-2xl"
                    style={{
                      backgroundImage: `url(${profile.avatar ?? ""})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1 p-6">
                  <h3 className="font-medium text-gray-900">
                    {profile?.firstName ?? ""} {profile?.lastName ?? ""}
                  </h3>
                  {/* <CoachRating coachUserId={uid} /> */}
                  <div className="dark:text-darkgray-600 text-sm text-gray-500 [&_a]:text-blue-500 [&_a]:underline [&_a]:hover:text-blue-600">
                    {profile.summary}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </Shell>
  );
}

function CoachRating({ coachUserId }: { coachUserId: number }) {
  const { data } = trpc.viewer.reviews.getRating.useQuery({ coachUserId });

  if (!data) {
    return <div className="h-5" />;
  }

  return (
    <div className="flex items-center">
      <Rating rating={data.rating} size="sm" />
      <span className="ml-2 text-sm text-gray-500">
        {data.rating.toFixed(1)} ({data.reviewCount})
      </span>
    </div>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { query } = context;

  // Check if any HubSpot parameters are present
  const hasHubspotParams = Object.keys(HUBSPOT_PARAMS).some((param) => param in query);
  if (hasHubspotParams) {
    // Map HubSpot parameters to internal filter parameters
    const goals: string[] = [];

    for (const [param, mapping] of Object.entries(HUBSPOT_PARAMS)) {
      const v = query[param];
      if (v) {
        const values = Array.isArray(v) ? v : [v];
        for (const value of values) {
          const result = mapping[String(value).trim()];
          if (result) {
            for (const goalId of result) {
              if (!goals.includes(goalId.toString())) {
                goals.push(goalId.toString());
              }
            }
          }
        }
      }
    }

    // Build the redirect URL with internal parameters
    const params = new URLSearchParams();
    for (const goal of goals) {
      params.append("goals", goal);
    }

    const queryString = params.toString();
    return {
      redirect: {
        destination: `/search${queryString ? `?${queryString}` : ""}`,
        permanent: false,
      },
    };
  }

  const ssr = await ssrInit(context);

  return {
    props: {
      trpcState: ssr.dehydrate(),
    },
  };
}
